import { useState, useEffect } from "react";
import { useForm } from "@tanstack/react-form";
import {
  TanstackFieldWrapper,
  TanstackFormControl,
} from "@/components/ui/tanstack-form";
import { Button } from "@/components/ui/button";
import { SaveActionButtons } from "@/components/shared/SaveActionButtons";
import { Input } from "@/components/ui/input";
import { NumericInput } from "@/components/shared/NumericInput";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Save,
  Loader2,
  Factory,
  Package,
  Trash2,
  Plus,
  ArrowDownToLine,
} from "lucide-react";
import { ComboboxFK } from "@/components/shared/ComboboxFK";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import { ComboboxConfig } from "@/components/shared/ComboboxConfig";
import { useProcessLookup, useMaterialLookup } from "@/hooks/useFKLookup";
import { useQueryClient } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { toast } from "sonner";
import { focusFirstError } from "@/lib/form/formUtils";
import { formatNumber, formatMoney } from "@/lib/utils";
import { F } from "@/components/shared/F";
import { useRoleAccess } from "@/hooks/useRoleAccess";

import { toVnDateString } from "@apv-erp/status";
import { defaultOutsourceUnit, OUTSOURCE_UNITS } from "@/lib/outsource-unit";
// ─── Types ──────────────────────────────
export interface OutsourcePOItemRow {
  productId: string;
  /** Dòng đơn hàng của dòng gia công — FQC / đóng gói / giao hàng gia công 100% theo dòng này (04/11/2026) */
  orderDetailId?: string | null;
  productName: string;
  productCode?: string | null; // mã SP: phân biệt các dòng cùng tên (FB-ZEVVL8)
  productSpecs: string; // chất liệu + quy trình
  quantity: number; // SL đặt gia công
  unit?: string; // ĐVT số lượng gửi gia công: tờ (in, cán, bế, lụa, nhũ) / pcs (dán…) — FB-CYG54A
  unitPrice: number; // Đơn giá gia công
  amount: number; // Thành tiền
  notes: string;
}

export interface OutsourcePOFormData {
  vendorId: string;
  orderId: string;
  issueDate: string;
  expectedDate: string;
  status: string;
  notes: string;
  deploymentModel?: string;
  productionOrderId?: string;
  /** Chuỗi công đoạn gửi gia công (id công đoạn LSX, theo thứ tự công nghệ) */
  routingStepIds?: string[];
  items: OutsourcePOItemRow[];
  filterDetailIds?: string[];
}

interface OutsourcePOFormProps {
  defaultValues?: Partial<OutsourcePOFormData>;
  onSubmit: (data: OutsourcePOFormData) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  submitLabel?: string;
  /** Sửa đơn đã có: đơn cũ chưa gắn đơn hàng / lệnh nào vẫn lưu được (máy chủ không bắt buộc) */
  isEdit?: boolean;
}

export default function OutsourcePOForm({
  defaultValues,
  onSubmit,
  onCancel,
  isSubmitting,
  submitLabel = "Lưu",
  isEdit = false,
}: OutsourcePOFormProps) {
  const [items, setItems] = useState<OutsourcePOItemRow[]>(
    defaultValues?.items || [],
  );
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [routingSteps, setRoutingSteps] = useState<any[]>([]);
  // Đơn hàng đang chọn: ô LSX chỉ liệt kê lệnh của đơn này (FB-3JUSMM)
  const [selectedOrderId, setSelectedOrderId] = useState<string>(defaultValues?.orderId ?? "");
  const [requiredErrors, setRequiredErrors] = useState<{ vendorId?: string; orderId?: string; productionOrderId?: string; routingStepIds?: string }>({});
  // Gia công một phần = gửi một số công đoạn của LSX → cần LSX của đơn hàng và công đoạn; toàn phần đặt thẳng từ đơn hàng
  const [deploymentModel, setDeploymentModel] = useState<string>(defaultValues?.deploymentModel ?? "OUTSOURCE_100");
  const isPartial = deploymentModel !== "OUTSOURCE_100";

  // Lưu nháp / Lưu (plan-draft-and-save-buttons-v1)
  const currentStatus = defaultValues?.status as string | undefined;
  const [isDraftSaving, setIsDraftSaving] = useState(false);

  const form = useForm({
    defaultValues: {
      vendorId: defaultValues?.vendorId ?? "",
      orderId: defaultValues?.orderId ?? "",
      issueDate:
        toVnDateString(defaultValues?.issueDate) ||
        toVnDateString(),
      expectedDate: toVnDateString(defaultValues?.expectedDate) || "",
      status: defaultValues?.status ?? "DRAFT",
      notes: defaultValues?.notes ?? "",
      deploymentModel: defaultValues?.deploymentModel ?? "OUTSOURCE_100",
      productionOrderId: defaultValues?.productionOrderId ?? "",
      routingStepIds: defaultValues?.routingStepIds ?? ([] as string[]),
    },
    onSubmit: async ({ value }) => {
      const data: OutsourcePOFormData = {
        ...value,
        issueDate: value.issueDate
          ? new Date(value.issueDate).toISOString()
          : new Date().toISOString(),
        expectedDate: value.expectedDate
          ? new Date(value.expectedDate).toISOString()
          : new Date().toISOString(),
        items,
      };
      await onSubmit({ ...data, saveMode: "SUBMIT" } as any);
    },
  });

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Bắt buộc nhà gia công và đơn hàng gốc trước khi gửi (trước đây thiếu NCC vẫn gửi → lỗi máy chủ, FB-3JUSMM)
    const v = form.state.values as any;
    const partial = (v.deploymentModel || "OUTSOURCE_100") !== "OUTSOURCE_100";
    const missing = {
      vendorId: v.vendorId ? undefined : "Chọn nhà gia công",
      // Cần đơn hàng gốc hoặc lệnh sản xuất (đơn cũ / lệnh lập tay không có đơn hàng vẫn lưu được — máy chủ không bắt buộc đơn hàng)
      orderId:
        v.orderId || v.productionOrderId || (isEdit && !defaultValues?.orderId && !defaultValues?.productionOrderId)
          ? undefined
          : "Chọn đơn hàng gốc (hoặc lệnh sản xuất)",
      productionOrderId: partial && !v.productionOrderId ? "Gia công một phần: chọn lệnh sản xuất của đơn hàng" : undefined,
      routingStepIds:
        partial && v.productionOrderId && !(v.routingStepIds || []).length ? "Chọn ít nhất 1 công đoạn gửi gia công" : undefined,
    };
    setRequiredErrors(missing);
    const messages = Object.values(missing).filter(Boolean);
    if (messages.length > 0) {
      toast.error(messages.join(" · "));
      return;
    }
    form.handleSubmit();
    requestAnimationFrame(() => focusFirstError(form.state as any));
  };

  // Lưu nháp: ghi tạm (chỉ cần nhà gia công), không kiểm tra đủ thông tin
  const handleSaveDraft = async () => {
    const value = form.state.values as any;
    if (!value.vendorId) {
      toast.error("Chọn nhà gia công trước khi lưu nháp");
      return;
    }
    setIsDraftSaving(true);
    try {
      await onSubmit({
        ...value,
        issueDate: value.issueDate ? new Date(value.issueDate).toISOString() : new Date().toISOString(),
        expectedDate: value.expectedDate ? new Date(value.expectedDate).toISOString() : new Date().toISOString(),
        items,
        saveMode: "DRAFT",
      } as any);
    } finally {
      setIsDraftSaving(false);
    }
  };

  const [isLoadingSteps, setIsLoadingSteps] = useState(false);
  const processLookup = useProcessLookup();
  const materialLookup = useMaterialLookup();
  const qc = useQueryClient();
  const { hasPermission } = useRoleAccess();

  // Combine both lookups to use in ComboboxFK (processes and materials)
  const combinedSpecsOptions = [
    ...processLookup.options.map((opt) => ({
      ...opt,
      meta: `Công đoạn ${opt.meta ? `(${opt.meta})` : ""}`,
    })),
    ...materialLookup.options.map((opt) => ({
      ...opt,
      meta: `Chất liệu ${opt.meta ? `(${opt.meta})` : ""}`,
    })),
  ];

  // Auto-load items when orderId is pre-filled
  useEffect(() => {
    if (defaultValues?.orderId && items.length === 0) {
      loadOrderItems(defaultValues.orderId, defaultValues.filterDetailIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues?.orderId]);

  // ─── Load order items when orderId changes ──────
  const loadOrderItems = async (
    orderId: string,
    filterDetailIds?: string[],
  ) => {
    if (!orderId) return;
    setLoadingOrder(true);
    try {
      const order = (await trpcClient.orders.getById.query({
        id: orderId,
      })) as any;
      if (!order) {
        toast.error("Không tìm thấy đơn hàng");
        return;
      }

      let details: any[] = order.orderDetails || order.items || [];
      if (filterDetailIds && filterDetailIds.length > 0) {
        details = details.filter((d: any) => filterDetailIds.includes(d.id));
      }

      const newItems: OutsourcePOItemRow[] = details.map((d: any) => {
        const specs = [d.materialName || d.product?.material?.name, d.process]
          .filter(Boolean)
          .join(" · ");
        const qty = Number(d.quantity) || 0;
        const price = Number(d.purchasePrice) || 0;
        return {
          productId: d.productId,
          orderDetailId: d.id || null,
          productName: d.productName || "—",
          productCode: d.productCode || d.product?.productCode || null,
          productSpecs: specs || d.specs || "",
          quantity: qty,
          unit: defaultOutsourceUnit(
            routingSteps.filter((st) => (form.getFieldValue("routingStepIds") || []).includes(st.id)),
          ),
          unitPrice: price,
          amount: qty * price,
          notes: "",
        };
      });
      setItems(newItems);
      if (newItems.length > 0) {
      }
    } catch (err: any) {
      toast.error(`Lỗi tải đơn hàng: ${err?.message || "unknown"}`);
    } finally {
      setLoadingOrder(false);
    }
  };

  // Load Routing Steps when productionOrderId changes
  const loadRoutingSteps = async (prodOrderId: string) => {
    if (!prodOrderId) {
      setRoutingSteps([]);
      return;
    }
    try {
      const po = (await trpcClient.productionOrders.getById.query({
        id: prodOrderId,
      })) as any;
      if (po && po.routingSteps) {
        setRoutingSteps(po.routingSteps);
      } else {
        setRoutingSteps([]);
      }
    } catch (err) {
      setRoutingSteps([]);
    }
  };

  // Auto-load routing steps if editing an existing PO with productionOrderId
  useEffect(() => {
    if (defaultValues?.productionOrderId) {
      loadRoutingSteps(defaultValues.productionOrderId);
    }
  }, [defaultValues?.productionOrderId]);

  // ─── Item helpers ──────
  const updateItem = (
    idx: number,
    field: keyof OutsourcePOItemRow,
    value: any,
  ) => {
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== idx) return it;
        const updated = { ...it, [field]: value };
        // Auto-calculate amount when qty or unitPrice changes
        if (field === "quantity" || field === "unitPrice") {
          updated.amount =
            (Number(updated.quantity) || 0) * (Number(updated.unitPrice) || 0);
        }
        return updated;
      }),
    );
  };
  const removeItem = (idx: number) =>
    setItems((prev) => prev.filter((_, i) => i !== idx));
  const addEmptyItem = () =>
    setItems((prev) => [
      ...prev,
      {
        productId: "",
        productName: "",
        productSpecs: "",
        quantity: 0,
        unitPrice: 0,
        amount: 0,
        notes: "",
      },
    ]);

  const totalQty = items.reduce((s, it) => s + (it.quantity || 0), 0);
  const totalAmount = items.reduce((s, it) => s + (it.amount || 0), 0);

  return (
    <>
      <div className="space-y-4 w-full mx-auto pb-8">
        <form onSubmit={handleFormSubmit} className="space-y-4">
          {/* ═══ HEADER INFO ═══ */}
          <Card className="border-none shadow-sm ring-1 ring-slate-200">
            <CardHeader className="bg-slate-50/50 py-3 border-b">
              <CardTitle className="text-sm font-bold flex items-center gap-2">
                <Factory className="h-4 w-4 text-orange-500" /> Thông tin đặt
                gia công
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6">
                <form.Field name="vendorId">
                  {(field: any) => (
                    <div className="col-span-1 md:col-span-2 space-y-1.5">
                      <Label className="text-xs font-semibold">
                        Đơn vị gia công{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <SharedCombobox
                        type="vendor"
                        value={field.state.value || ""}
                        valueLabel={(defaultValues as any)?.vendor?.name || (defaultValues as any)?.vendor?.shortName}
                        onChange={(id) => {
                          field.handleChange(id);
                          if (id) setRequiredErrors((e) => ({ ...e, vendorId: undefined }));
                        }}
                        placeholder="Chọn NCC..."
                        canAdd={hasPermission("vendors", "create")}
                        className="h-9"
                      />
                      {requiredErrors.vendorId && (
                        <p className="text-xs text-destructive" data-testid="outsource-vendor-error">{requiredErrors.vendorId}</p>
                      )}
                    </div>
                  )}
                </form.Field>
                <form.Field name="orderId">
                  {(field: any) => (
                    <div className="col-span-1 space-y-1.5">
                      <Label className="text-xs font-semibold">
                        Đơn hàng gốc <span className="text-destructive">*</span>
                      </Label>
                      <SharedCombobox
                        type="order"
                        value={field.state.value || ""}
                        valueLabel={(defaultValues as any)?.order?.orderId || (defaultValues as any)?.order?.code || (defaultValues as any)?.orderId}
                        onChange={(id) => {
                          field.handleChange(id);
                          setSelectedOrderId(id || "");
                          if (id) setRequiredErrors((e) => ({ ...e, orderId: undefined }));
                          // Đổi đơn hàng: bỏ LSX / công đoạn đang chọn (LSX phải thuộc đúng đơn hàng)
                          if (form.getFieldValue("productionOrderId")) {
                            form.setFieldValue("productionOrderId", "");
                            form.setFieldValue("routingStepIds", []);
                            setRoutingSteps([]);
                          }
                          if (id) loadOrderItems(id);
                        }}
                        placeholder="Chọn ĐH..."
                        className="h-9"
                      />
                      {requiredErrors.orderId && (
                        <p className="text-xs text-destructive">{requiredErrors.orderId}</p>
                      )}
                      {loadingOrder && (
                        <div className="flex items-center gap-1 text-xs text-orange-500">
                          <Loader2 className="h-3 w-3 animate-spin" /> Đang tải
                          SP...
                        </div>
                      )}
                    </div>
                  )}
                </form.Field>
                <form.Field name="productionOrderId">
                  {(field: any) => (
                    <div className="col-span-1 space-y-1.5">
                      <Label className="text-xs font-semibold">
                        Lệnh sản xuất
                      </Label>
                      <SharedCombobox
                        type="productionOrder"
                        params={selectedOrderId ? { ofOrderId: selectedOrderId } : undefined}
                        value={field.state.value || ""}
                        valueLabel={(defaultValues as any)?.productionOrder?.lsxId || (defaultValues as any)?.productionOrder?.id}
                        onChange={(id) => {
                          field.handleChange(id);
                          form.setFieldValue("routingStepIds", []);
                          if (id) setRequiredErrors((e) => ({ ...e, productionOrderId: undefined }));
                          loadRoutingSteps(id);
                        }}
                        placeholder={selectedOrderId ? "Chọn LSX của đơn hàng..." : "Chọn LSX..."}
                        className="h-9"
                      />
                      {requiredErrors.productionOrderId && (
                        <p className="text-xs text-destructive" data-testid="outsource-lsx-error">{requiredErrors.productionOrderId}</p>
                      )}
                      {isPartial && !field.state.value && (
                        <p className="text-[11px] text-muted-foreground" data-testid="outsource-partial-hint">
                          Gia công một phần cần lệnh sản xuất của đơn hàng.{" "}
                          {selectedOrderId ? (
                            <a className="text-primary underline" href={`/orders/${selectedOrderId}`}>
                              Chưa có lệnh? Lập lệnh SX ở đơn hàng
                            </a>
                          ) : (
                            "Chọn đơn hàng trước."
                          )}
                        </p>
                      )}
                    </div>
                  )}
                </form.Field>

                <form.Field name="routingStepIds">
                  {(field: any) => {
                    const selected: string[] = field.state.value || [];
                    const chain = routingSteps.filter((st) => selected.includes(st.id));
                    const toggle = (id: string) => {
                      const next = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
                      // Giữ đúng thứ tự công nghệ của LSX
                      const nextChain = routingSteps.filter((st) => next.includes(st.id));
                      field.handleChange(nextChain.map((st) => st.id));
                      // ĐVT theo công đoạn gửi đi (tờ / pcs); người dùng vẫn sửa được từng dòng
                      const unit = defaultOutsourceUnit(nextChain);
                      setItems((prev) => prev.map((it) => ({ ...it, unit })));
                    };
                    return (
                      <div className="col-span-1 md:col-span-4 space-y-1.5">
                        <Label className="text-xs font-semibold">
                          Công đoạn gia công
                        </Label>
                        {routingSteps.length === 0 ? (
                          <div className="h-9 flex items-center text-sm text-muted-foreground">
                            Chọn LSX để chọn công đoạn
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-1.5" data-testid="outsource-step-picker">
                            {routingSteps.map((step) => {
                              const on = selected.includes(step.id);
                              return (
                                <button
                                  key={step.id}
                                  type="button"
                                  data-testid={`outsource-step-chip-${step.id}`}
                                  aria-pressed={on}
                                  onClick={() => toggle(step.id)}
                                  className={`h-8 px-2.5 rounded-md border text-xs ${on ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted"}`}
                                >
                                  {step.stepOrder}. {step.name}
                                </button>
                              );
                            })}
                          </div>
                        )}
                        {chain.length > 0 && (
                          <div className="text-xs text-muted-foreground" data-testid="outsource-step-chain">
                            Gửi gia công: {chain.map((st) => st.name).join(" → ")}
                          </div>
                        )}
                        {requiredErrors.routingStepIds && (
                          <p className="text-xs text-destructive">{requiredErrors.routingStepIds}</p>
                        )}
                      </div>
                    );
                  }}
                </form.Field>
                <form.Field name="deploymentModel">
                  {(field: any) => (
                    <div className="col-span-1 space-y-1.5">
                      <Label className="text-xs font-semibold">
                        Loại gia công
                      </Label>
                      <Select
                        value={field.state.value}
                        onValueChange={(val) => {
                          field.handleChange(val);
                          setDeploymentModel(val);
                          setRequiredErrors((e) => ({ ...e, productionOrderId: undefined, routingStepIds: undefined }));
                        }}
                      >
                        <SelectTrigger className="h-9 text-sm" data-testid="outsource-model">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="OUTSOURCE_100">
                            Toàn phần (NCC làm trọn sản phẩm)
                          </SelectItem>
                          <SelectItem value="HYBRID_FINISH_EXTERNAL">
                            Một phần (công đoạn của LSX)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </form.Field>
                <div className="col-span-1">
                  <F
                    form={form}
                    name="issueDate"
                    label="Ngày phát hành"
                    type="date"
                    required
                  />
                </div>
                <div className="col-span-1">
                  <F
                    form={form}
                    name="expectedDate"
                    label="Hạn trả hàng"
                    type="date"
                    required
                  />
                </div>

                <div className="col-span-1 md:col-span-3">
                  <F
                    form={form}
                    name="notes"
                    label="Ghi chú"
                    placeholder="Ghi chú cho NCC..."
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ═══ ITEMS TABLE ═══ */}
          <Card className="border-none shadow-sm ring-1 ring-slate-200 overflow-hidden">
            <CardHeader className="bg-slate-50/50 py-3 border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-bold flex items-center gap-2">
                  <Package className="h-4 w-4 text-orange-500" /> Chi tiết gia
                  công ({items.length} sản phẩm)
                </CardTitle>
                {totalAmount > 0 && (
                  <span className="text-sm font-bold text-orange-600">
                    Tổng: {formatMoney(totalAmount)}
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {items.length === 0 ? (
                <div className="text-center py-8 text-sm text-slate-400">
                  <ArrowDownToLine className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  Chọn Đơn hàng ở trên để tự động tải sản phẩm
                </div>
              ) : (
                <div className="overflow-x-auto min-h-[200px]">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-orange-50/50 text-[11px] uppercase tracking-wider">
                        <TableHead className="w-10 text-center">STT</TableHead>
                        <TableHead className="min-w-[160px]">
                          Sản phẩm
                        </TableHead>
                        <TableHead className="min-w-[120px]">
                          Chất liệu / QT
                        </TableHead>
                        <TableHead className="w-24 text-right">
                          SL Đặt
                        </TableHead>
                        <TableHead className="w-20">ĐVT</TableHead>
                        <TableHead className="w-32 text-right text-orange-600 font-bold">
                          Đơn giá GC
                        </TableHead>
                        <TableHead className="w-28 text-right text-orange-600 font-bold">
                          Thành tiền
                        </TableHead>
                        <TableHead className="min-w-[150px]">Ghi chú</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map((item, idx) => (
                        <TableRow
                          key={idx}
                          className="group hover:bg-slate-50/60 transition-colors"
                        >
                          <TableCell className="text-center text-slate-400 text-xs align-top pt-4">
                            {idx + 1}
                          </TableCell>
                          <TableCell className="align-top pt-2">
                            <Input
                              value={item.productName}
                              onChange={(e) =>
                                updateItem(idx, "productName", e.target.value)
                              }
                              className="h-8 text-sm border-slate-200 bg-white min-w-[150px]"
                              placeholder="Tên SP..."
                            />
                            {item.productCode && (
                              <div className="mt-0.5 px-1 text-[11px] font-mono font-semibold text-indigo-700" data-testid="outsource-item-code">
                                {item.productCode}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="align-top pt-2">
                            <ComboboxConfig
                              value={item.productSpecs}
                              onChange={(val) => {
                                // When selected or quick-added, use the string label directly
                                const label =
                                  combinedSpecsOptions.find((o) => o.id === val)
                                    ?.label || val;
                                // Append if existing
                                if (!item.productSpecs)
                                  updateItem(idx, "productSpecs", label);
                                else
                                  updateItem(
                                    idx,
                                    "productSpecs",
                                    `${item.productSpecs} · ${label}`,
                                  );
                              }}
                              options={combinedSpecsOptions}
                              loading={
                                processLookup.isLoading ||
                                materialLookup.isLoading
                              }
                              hideLabel
                              placeholder="Chất liệu/QT..."
                              quickAddLabel="Nhập text tự do"
                              onQuickAdd={async (text) => text}
                              className="h-8 text-sm"
                            />
                          </TableCell>
                          <TableCell className="align-top pt-2">
                            <NumericInput
                              min={0}
                              value={item.quantity || ""}
                              onChange={(v) =>
                                updateItem(idx, "quantity", Number(v) || 0)
                              }
                              className="h-8 text-sm text-right tabular-nums w-20 ml-auto border-slate-200"
                            />
                          </TableCell>
                          <TableCell className="align-top pt-2">
                            <select
                              value={item.unit || "pcs"}
                              onChange={(e) => updateItem(idx, "unit", e.target.value)}
                              className="h-8 text-sm border border-slate-200 rounded-md px-1.5 bg-white"
                              data-testid={`outsource-item-unit-${idx}`}
                              aria-label="Đơn vị tính"
                            >
                              {OUTSOURCE_UNITS.map((u) => (
                                <option key={u} value={u}>
                                  {u}
                                </option>
                              ))}
                              {item.unit && !(OUTSOURCE_UNITS as readonly string[]).includes(item.unit) && (
                                <option value={item.unit}>{item.unit}</option>
                              )}
                            </select>
                          </TableCell>
                          <TableCell className="align-top pt-2">
                            <NumericInput
                              min={0}
                              value={item.unitPrice || ""}
                              onChange={(v) =>
                                updateItem(idx, "unitPrice", Number(v) || 0)
                              }
                              className="h-8 text-sm text-right tabular-nums w-28 ml-auto font-semibold text-orange-700 border-slate-200"
                              placeholder="0"
                            />
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-sm font-semibold text-orange-600 align-top pt-4">
                            {item.amount > 0 ? formatMoney(item.amount) : "—"}
                          </TableCell>
                          <TableCell className="align-top pt-2">
                            <Input
                              value={item.notes}
                              onChange={(e) =>
                                updateItem(idx, "notes", e.target.value)
                              }
                              className="h-8 text-xs border-slate-200 bg-white"
                              placeholder="Ghi chú SP..."
                            />
                          </TableCell>
                          <TableCell className="align-top pt-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-slate-400 hover:text-rose-500 hover:bg-rose-50"
                              onClick={() => removeItem(idx)}
                              title="Xóa dòng"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Add Row — between items and totals */}
                      <TableRow>
                        <TableCell colSpan={9} className="px-2 py-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={addEmptyItem}
                            className="w-full text-orange-600 hover:bg-orange-50 border border-dashed border-orange-200 h-8 text-xs"
                          >
                            <Plus className="h-3.5 w-3.5 mr-1" /> Thêm dòng
                          </Button>
                        </TableCell>
                      </TableRow>
                      {/* Totals */}
                      <TableRow className="bg-orange-50/60 border-t-2 font-semibold text-xs">
                        <TableCell
                          colSpan={3}
                          className="text-right text-slate-500 py-3"
                        >
                          TỔNG
                        </TableCell>
                        <TableCell className="text-right tabular-nums py-3 text-slate-700">
                          {formatNumber(totalQty)}
                        </TableCell>
                        <TableCell className="py-3"></TableCell>
                        <TableCell className="py-3"></TableCell>
                        <TableCell className="text-right tabular-nums text-orange-700 text-sm py-3">
                          {formatMoney(totalAmount)}
                        </TableCell>
                        <TableCell colSpan={2} className="py-3"></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ═══ ACTIONS ═══ */}
          <div className="sticky bottom-0 z-50 -mx-4 sm:-mx-6 -mb-4 sm:-mb-6 p-4 sm:p-6 bg-white/95 backdrop-blur-md border-t border-slate-200 mt-6 flex justify-start gap-3 shadow-[0_-4px_6px_-1px_rgb(0,0,0,0.05)]">
            <form.Subscribe
              selector={(s: any) => [s.canSubmit, s.isSubmitting]}
            >
              {([canSubmit, submitting]: any) => (
                <SaveActionButtons
                  status={currentStatus}
                  busy={Boolean(submitting || isSubmitting || isDraftSaving)}
                  onSaveDraft={handleSaveDraft}
                  submitLabel={submitLabel}
                />
              )}
            </form.Subscribe>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="min-w-[100px]"
            >
              Huỷ
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}
